import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Container } from 'reactstrap';
import classnames from 'classnames';

// Data and model
import { connect } from 'react-redux';
import { ModelPreloader } from 'client/data/luckdragon/redux/model-preloader';
import { connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { buildPubStateModelYearPath, VehicleModel } from 'client/data/models/vehicle-v2';
import { CmsEntities, CmsModel, bindToContent } from 'client/data/models/cms';
import { PageModel } from 'client/data/models/page';
import { MultiOfferModel, partnersBrandAssetsPath } from 'client/data/models/multi-offer';
import { DEFAULT_CONTENT } from 'client/data/cms/content';
import { setCriticalCss } from 'client/actions/critical-css';

// Tracking
import { TrackingHandler } from 'client/tracking/handler';
import { AppraisalMap } from 'client/tracking/maps/appraisal';

// Utils
import { pageDefinition } from 'site-modules/shared/pages/page';
import { isAddressToVinExcludedState } from 'site-modules/shared/components/appraisal/appraisal-tabs-v2/utils/is-address-to-vin-excluded-state';
import { getInitialTabIdFromLocation } from 'site-modules/shared/components/appraisal/appraisal-tabs-v2/utils/get-initial-tab-id-from-location';
import {
  getAppraisalPromotionsBackground,
  getAppraisalPromotionsHeadingTextClass,
  getAppraisalPromotionsHeaderText,
} from 'site-modules/appraisal/utils/appraisal-promotions';

// Constants
import { TESTIMONIALS_CONTENT_PATH } from 'site-modules/shared/constants/multi-offer/editorial';
import { PUB_STATES } from 'client/constants/pub-states';

// Components
import { AppraisalTabsContainer } from 'site-modules/shared/components/appraisal/appraisal-tabs-container/appraisal-tabs-container';
import { AppraisalPromotionsInfoSection } from 'site-modules/appraisal/components/appraisal-promotions-info-section/appraisal-promotions-info-section';
import { AppraisalPromotionsHowItWorks } from 'site-modules/appraisal/components/appraisal-promotions-how-it-works/appraisal-promotions-how-it-works';
import { ErrorBoundary } from 'site-modules/shared/components/error-boundary/error-boundary';
import { AppraisalPromotionsBackground } from 'site-modules/appraisal/components/appraisal-promotions-background/appraisal-promotions-background';
import { AppraisalPromotionsPartners } from 'site-modules/appraisal/components/appraisal-promotions-partners/appraisal-promotions-partners';
import { AppraisalPromotionsWhyEdmunds } from 'site-modules/appraisal/components/appraisal-promotions-why-edmunds/appraisal-promotions-why-edmunds';

import { EdmundsNoAdsDecorator } from 'client/site-modules/shared/pages/decorators/edmunds-no-ads';

import 'site-modules/shared/components/appraisal/appraisal-common.scss';
import './appraisal-promotions.scss';
import './appraisal-promotions-landing.scss';
import dodgersEdmundsLogo from './dodgers-edmunds-logo.svg';

const APPRAISAL_PROMOTIONS_CONTENT_FILE_PATH = 'appraisal/promotions/tripleplay/index';

const PROMO_VALUE = undefined; // this is set to a specific value when promo campaign is active.

export const AppraisalPromotionsUI = ({
  isMobile = false,
  visitorLocation = {},
  testimonials = DEFAULT_CONTENT,
  location = {},
  content = DEFAULT_CONTENT,
}) => {
  useEffect(() => {
    TrackingHandler.useMap(AppraisalMap);
  }, []);

  const renderAddressTab = !isAddressToVinExcludedState(visitorLocation);
  const { HEADER, SUBHEADER } = getAppraisalPromotionsHeaderText(PROMO_VALUE);
  const headingTextClass = getAppraisalPromotionsHeadingTextClass(PROMO_VALUE);

  return (
    <main id="main-content" className="appraisal-promotions">
      <div className="appraisal-promotions-landing pos-r mb-3 bg-white mx-auto">
        <AppraisalPromotionsBackground promoValue={PROMO_VALUE} />
        <Container fluid className="pt-3_5">
          <div className="content">
            <header
              className={classnames('heading-section mb-2', {
                'heading-section-promos': PROMO_VALUE,
                'text-center text-sm-start': !PROMO_VALUE,
              })}
            >
              {!PROMO_VALUE && (
                <img
                  src={dodgersEdmundsLogo}
                  alt="Dodgers and Edmunds logo"
                  height={100}
                  className="logos d-inline-block"
                />
              )}
              <div>
                <h1 className={`fw-bold text-white ${headingTextClass} mb-0_5 mt-1 mt-lg-0`}>{HEADER}</h1>
                <h2 className={`text-white fw-normal ${headingTextClass} mb-0`}>{SUBHEADER}</h2>
              </div>
            </header>
            <AppraisalTabsContainer
              isBackgroundStyleTabEnabled
              isFullWidth
              isMobile={isMobile}
              isProfileOfferVinPrepopulationEnabled
              hasAddressToVinDrawer={renderAddressTab}
              isAppraisalLanding
              ctaBtnColor="blue-50"
              renderAddressTab={renderAddressTab}
            />
            <ErrorBoundary>
              <AppraisalPromotionsHowItWorks />
            </ErrorBoundary>
            <AppraisalPromotionsPartners />
          </div>
        </Container>
        <div className="bg-blue-100">
          <div className="content">
            <div className="px-0_5 px-md-0">
              <ErrorBoundary>
                <AppraisalPromotionsWhyEdmunds />
              </ErrorBoundary>
            </div>
          </div>
        </div>
        <Container fluid>
          <div className="content">
            <ErrorBoundary>
              <AppraisalPromotionsInfoSection
                testimonials={testimonials}
                isMobile={isMobile}
                faqContent={content.child('faq')}
                pathname={location.pathname}
              />
            </ErrorBoundary>
          </div>
        </Container>
      </div>
    </main>
  );
};

AppraisalPromotionsUI.propTypes = {
  isMobile: PropTypes.bool,
  visitorLocation: PropTypes.shape({}),
  testimonials: CmsEntities.Content,
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }),
  content: CmsEntities.Content,
};

export const mapStateToProps = state => ({
  isMobile: state.mobile,
  visitorLocation: state.visitor?.location,
});

export const stateToPropsConfig = {
  content: bindToContent(APPRAISAL_PROMOTIONS_CONTENT_FILE_PATH),
  testimonials: bindToContent(TESTIMONIALS_CONTENT_PATH, 'testimonials'),
};

export const AppraisalPromotions = connect(mapStateToProps)(connectToModel(AppraisalPromotionsUI, stateToPropsConfig));

export const pageDefinitionConfiguration = {
  name: 'used_cars_tmv_appraiser_promotions',
  category: 'used_cars_tmv_appraiser',
  preload: store => {
    const preloader = new ModelPreloader(store);

    preloader.resolve(`content["${APPRAISAL_PROMOTIONS_CONTENT_FILE_PATH}"]`, CmsModel);
    preloader.resolve(`content["${TESTIMONIALS_CONTENT_PATH}"]`, CmsModel);
    preloader.resolve(buildPubStateModelYearPath([PUB_STATES.NEW_USED, PUB_STATES.USED]), VehicleModel);
    preloader.resolve(partnersBrandAssetsPath.build(), MultiOfferModel);

    const { DESKTOP, MOBILE } = getAppraisalPromotionsBackground(PROMO_VALUE);

    preloader.set('server.preloadedAssets', PageModel, [
      {
        href: DESKTOP,
        as: 'image',
        type: 'image/svg+xml',
      },
      {
        href: MOBILE,
        as: 'image',
        type: 'image/svg+xml',
      },
    ]);

    return preloader.load().then(() => {
      const {
        pageContext: { location, page },
      } = store.getState();
      const initialTabId = getInitialTabIdFromLocation({
        location,
        isLicensePlateTabDefault: false,
        isSellCar: false,
        referer: page?.referer,
      });
      store.dispatch(setCriticalCss({ initialTabId }));
    });
  },
  decorator: EdmundsNoAdsDecorator,
  seo: { robots: { noIndex: true, noFollow: true } },
  component: AppraisalPromotions,
};

export default pageDefinition(pageDefinitionConfiguration);
